import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PROJECTS, TESTIMONIALS } from '../constants';
import { CLIENT_META } from '../constants/clientMeta';
import ScrollWords from '../components/ScrollWords';

const EASE = [0.22, 1, 0.36, 1];

function Shot({ project, className = '' }) {
  const [broken, setBroken] = useState(false);
  const web = project.type === 'web';
  if (broken) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#4FB0E7]/30 to-[#07101F]">
        <span className="display text-5xl text-white/80">{project.title[0]}</span>
      </div>
    );
  }
  if (project.framed) {
    return (
      <img
        src={project.image}
        alt={`${project.title} website on laptop and phone`}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        className={`absolute inset-0 h-full w-full object-cover ${className}`}
      />
    );
  }
  // laptop + phone mockups: shown whole, floating on a soft blue glow
  if (project.mockup) {
    return (
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-[radial-gradient(ellipse_60%_55%_at_50%_55%,rgba(79,176,231,0.28),transparent_70%)] px-4 py-3 sm:px-6 sm:py-5">
        <img
          src={project.image}
          alt={`${project.title} website on laptop and phone`}
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className={`mock-float max-h-full w-auto max-w-full object-contain drop-shadow-[0_24px_40px_rgba(0,0,0,0.55)] ${className}`}
        />
      </div>
    );
  }
  return web ? (
    <img
      src={project.image}
      alt={project.title}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
      className={`h-full w-full object-cover object-top ${className}`}
    />
  ) : (
    <div className="absolute inset-0 flex items-center justify-center bg-[#091323] p-6">
      <img
        src={project.image}
        alt={project.title}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        className={`max-h-full w-auto object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.55)] ${className}`}
      />
    </div>
  );
}

function ClientModal({ project, onClose }) {
  const meta = CLIENT_META[project.id];

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    window.lenis?.stop();
    return () => {
      window.removeEventListener('keydown', onKey);
      window.lenis?.start();
    };
  }, [onClose]);

  return (
    <motion.div
      data-liquid-ignore
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999] flex items-end justify-center bg-[#07101F]/75 backdrop-blur-md sm:items-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-modal-title"
    >
      <motion.div
        initial={{ y: 40, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        data-lenis-prevent
        className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-[2rem] border border-white/15 bg-[#0B1628] text-white shadow-2xl sm:rounded-[2rem]"
      >
        <button
          type="button"
          onClick={onClose}
          autoFocus
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 transition-colors hover:bg-black/60"
        >
          <X size={18} />
        </button>
        <div className="relative aspect-[16/10] overflow-hidden bg-black/40">
          <Shot project={project} />
        </div>
        <div className="p-7 sm:p-9">
          <h3 id="client-modal-title" className="display-semi text-3xl">
            {project.title}
          </h3>
          <dl className="mt-6 space-y-4">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">Problem</dt>
              <dd className="mt-1 text-lg text-slate-200">{meta?.problem}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#3DDC97]">What we built</dt>
              <dd className="mt-1 text-lg font-medium">{meta?.built}</dd>
            </div>
          </dl>
          {reviewFor(project.id) && (
            <figure className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
              {reviewFor(project.id).rating && <p className="mb-2 tracking-[0.2em] text-[#F5B529]" aria-label="5 out of 5 stars">{'\u2605'.repeat(reviewFor(project.id).rating)}</p>}
              <blockquote className="italic leading-relaxed text-white/90">&ldquo;{reviewFor(project.id).content}&rdquo;</blockquote>
              <figcaption className="mt-3 text-sm text-white/60">
                {reviewFor(project.id).name} &middot; {reviewFor(project.id).role}
              </figcaption>
            </figure>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            {project.liveUrl && (
              <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2">
                Visit website <ExternalLink size={16} aria-hidden="true" />
              </a>
            )}
            <Link to="/contact" onClick={onClose} className="btn border border-white/20 bg-white/10 text-white hover:bg-white/20">
              Start a similar project
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

const pad = (n) => String(n).padStart(2, '0');
const reviewFor = (id) => TESTIMONIALS.find((t) => t.projectId === id);

// each client's brand colour lights the stage when it comes to the front
const BRAND = {
  8: '#E3262C', // Mishkat: red
  1: '#3D6BFF', // Super Tech: royal blue
  7: '#F7931E', // AABEX: orange
  4: '#F5B529', // Travel Mart: gold
  2: '#22C55E', // Sabta: green
  3: '#4FB0E7', // Mumineen Dua: Farwings blue
};


// The floor reflection is a flipped copy under each picture: static, so the browser paints it
// once (the CSS box-reflect effect and drop-shadow filters were re-painted on every scroll frame).
// the picture that sits on the ring: device mockups float free, app screenshots get a phone frame
function RingShot({ project }) {
  const alt = project.mockup || project.framed ? `${project.title} website on laptop and phone` : `${project.title} screen`;
  const pic = (decorative) => {
    const a = decorative ? '' : alt;
    if (project.framed) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <img src={project.image} alt={a} loading="lazy" decoding="async" data-ring-img="" draggable={false} className="photo-fade block h-auto max-h-full w-auto max-w-full select-none" />
        </div>
      );
    }
    if (project.mockup) {
      return <img src={project.image} alt={a} loading="lazy" decoding="async" data-ring-img="" draggable={false} className="h-full w-full select-none object-contain" />;
    }
    return (
      <div className="mx-auto flex h-full items-center justify-center">
        <div className="h-full overflow-hidden rounded-[2rem] border-[6px] border-[#1d2433] bg-black">
          <img src={project.image} alt={a} loading="lazy" decoding="async" data-ring-img="" draggable={false} className="h-full w-auto select-none object-contain" />
        </div>
      </div>
    );
  };
  return (
    <div className="relative h-full w-full">
      {pic(false)}
      <div aria-hidden="true" className="ring-mirror pointer-events-none absolute inset-x-0 top-full h-full">
        <div className="h-full w-full" style={{ transform: 'scaleY(-1)' }}>
          {pic(true)}
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const list = PROJECTS;
  const total = list.length;
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState(null);
  const [still, setStill] = useState(false); // reduced motion: plain list
  const close = useCallback(() => setSelected(null), []);

  const pinRef = useRef(null);
  const itemsRef = useRef([]);
  const glowRef = useRef(null);
  const namesRef = useRef(null);
  const barRef = useRef(null);
  const stRef = useRef(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setStill(true);
      return undefined;
    }
    let target = 0;
    let cur = 0;
    let shownActive = 0;
    let raf = 0;
    let last = performance.now();

    const render = (p) => {
      const fade = window.innerWidth < 640 ? 1.15 : 0.42; // phones: only the front client shows
      itemsRef.current.forEach((el, i) => {
        if (!el) return;
        const d = i - p;
        const ad = Math.abs(d);
        // set the transform itself (not a CSS variable) so the browser restyles one element, not its whole subtree
        el.style.transform = `translate(-50%, -50%) rotateY(${(d * 38).toFixed(3)}deg) translateZ(var(--R))`;
        const op = Math.max(0, 1 - Math.max(0, ad - 0.15) * fade);
        el.style.opacity = op.toFixed(3);
        el.style.visibility = op < 0.01 ? 'hidden' : 'visible'; // fully faded clients are not painted at all
        el.style.zIndex = String(100 - Math.round(ad * 10));
        el.style.pointerEvents = ad < 0.5 ? 'auto' : 'none';
      });
      if (namesRef.current) {
        // only the client in front shows its name; neighbours fade out instead of peeking in
        Array.from(namesRef.current.children).forEach((el, i) => {
          const d = i - p;
          const ad = Math.min(Math.abs(d), 1);
          el.style.opacity = String(Math.max(0, 1 - Math.abs(d) * 1.7).toFixed(3));
          el.style.transform = `translate3d(${(-d * 6).toFixed(2)}%, 0, 0) scale(${(1 - ad * 0.08).toFixed(3)})`;
        });
      }
      if (barRef.current) barRef.current.style.transform = `scaleX(${total > 1 ? p / (total - 1) : 1})`;
      const a = Math.round(p);
      if (a !== shownActive) {
        shownActive = a;
        setActive(a);
        // each client has its own glow layer in its brand colour; only opacities cross-fade (GPU only)
        glowRef.current?.querySelectorAll('[data-glow]').forEach((g, gi) => {
          g.style.opacity = gi === a ? '1' : '0';
        });
      }
    };

    // runs only while the ring is moving, then sleeps until the next scroll
    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      cur += (target - cur) * (1 - Math.exp(-dt * 9)); // smooth follow
      const settled = Math.abs(target - cur) < 0.0005;
      if (settled) cur = target;
      render(cur);
      raf = settled ? 0 : requestAnimationFrame(loop);
    };
    const wake = () => {
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(loop);
    };

    // warm the pictures two screens before the ring: download + decode them now, so a client turning
    // into view never has to load or unpack its image mid-animation (that was the stutter on phones)
    let warmIO;
    const warm = () => {
      pinRef.current?.querySelectorAll('img[data-ring-img]').forEach((img) => {
        img.loading = 'eager';
        if (img.decode) img.decode().catch(() => {});
      });
    };
    if (typeof IntersectionObserver !== 'undefined' && pinRef.current) {
      warmIO = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            warm();
            warmIO.disconnect();
          }
        },
        { rootMargin: '200% 0px' }
      );
      warmIO.observe(pinRef.current);
    } else warm();

    const st = ScrollTrigger.create({
      trigger: pinRef.current,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * 0.75 * (total - 1))}`,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      snap: total > 1 ? { snapTo: 1 / (total - 1), directional: false, duration: { min: 0.25, max: 0.7 }, delay: 0.08, ease: 'power2.inOut' } : undefined,
      onUpdate: (self) => {
        target = self.progress * (total - 1);
        wake();
      },
    });
    stRef.current = st;
    shownActive = -1;
    render(0);
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);

    return () => {
      cancelAnimationFrame(raf);
      warmIO?.disconnect();
      window.removeEventListener('load', refresh);
      st.kill();
      stRef.current = null;
    };
  }, [list, total]);

  // jump to a client: scroll to its point in the pinned range
  const goTo = (i) => {
    const st = stRef.current;
    if (!st) return;
    const y = st.start + ((st.end - st.start) * i) / Math.max(1, total - 1);
    if (window.lenis) window.lenis.scrollTo(y, { duration: 1.2 });
    else window.scrollTo({ top: y, behavior: 'smooth' });
  };

  // the chat assistant can ask the ring to show a client ("show me Sabta")
  useEffect(() => {
    const onClient = (e) => {
      const i = list.findIndex((p) => p.id === e.detail?.id);
      if (i < 0) return;
      if (stRef.current) goTo(i);
      else setActive(i);
    };
    window.addEventListener('fw:client', onClient);
    return () => window.removeEventListener('fw:client', onClient);
  });

  const cur = list[active] || list[0];
  const meta = CLIENT_META[cur.id] || {};

  return (
    <section id="work" aria-labelledby="work-title" className="relative w-full bg-[#040A15] text-white">
      <div className="relative z-10 pt-24 sm:pt-32">
        <ScrollWords id="work-title" accent="clients">
          Our
        </ScrollWords>
        <p className="frame text-sm font-semibold uppercase tracking-[0.3em] text-white/70">India &middot; UAE &middot; Kuwait &middot; Bahrain</p>
      </div>

      {still ? (
        /* reduced motion: a calm list */
        <div className="frame grid gap-10 py-16 md:grid-cols-2">
          {list.map((p, i) => {
            const m = CLIENT_META[p.id] || {};
            return (
              <article key={p.id} className="rounded-[1.75rem] border border-white/10 bg-white/[0.03] p-6">
                <div className="relative aspect-[16/10]">
                  <RingShot project={p} />
                </div>
                <p className="mt-6 text-sm font-semibold" style={{ color: BRAND[p.id] }}>
                  {pad(i + 1)} &middot; {m.place}
                </p>
                <h3 className="display mt-2 text-3xl">{p.title}</h3>
                <p className="mt-3 text-neutral-300">{m.problem}</p>
                <p className="mt-1 font-medium">{m.built}</p>
              </article>
            );
          })}
        </div>
      ) : (
        <div ref={pinRef} className="ring-pin relative flex h-[100svh] flex-col overflow-hidden pb-6 pt-24 lg:pt-28">
          {/* stage light in the client's brand colour */}
          <div ref={glowRef} aria-hidden="true" className="pointer-events-none absolute" style={{ inset: '10% 16% 24% 16%' }}>
            {/* one compact glow per client; switching clients only fades opacity, so nothing restyles or repaints */}
            {list.map((p, i) => (
              <div
                key={p.id}
                data-glow=""
                className="absolute inset-0 transition-opacity duration-700 ease-out"
                style={{
                  opacity: i === 0 ? 1 : 0,
                  background: `radial-gradient(closest-side, color-mix(in srgb, ${BRAND[p.id] || '#4FB0E7'} 32%, transparent), transparent)`,
                }}
              />
            ))}
          </div>
          {/* floor line */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-[62%] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent sm:top-[64%]" />

          {/* the full name of the client in front, glowing in its brand colour, above the ring */}
          <div aria-hidden="true" ref={namesRef} className="pointer-events-none relative z-0 mx-auto mt-2 h-[10vw] w-full max-w-[96vw] sm:mt-0 sm:h-[9vw] lg:h-[7.6vw]">
            {list.map((p, i) => (
              <span
                key={p.id}
                className="ring-name display absolute inset-x-0 top-0 block whitespace-nowrap text-center leading-[0.95] will-change-transform"
                style={{
                  '--gc': BRAND[p.id] || '#4FB0E7', // each name keeps its own brand colour, so nothing animates colour
                  fontSize: `min(8.2vw, ${(92 / (p.title.length * 0.47)).toFixed(2)}vw)`,
                  opacity: i === 0 ? 1 : 0,
                }}
              >
                {p.title}
              </span>
            ))}
          </div>

          {/* the 3D ring */}
          <div className="ring-stage relative mx-auto mt-2 h-[36%] w-full sm:-mt-[1vw] sm:h-[44%]">
            <div className="ring-orbit absolute inset-0">
              {list.map((p, i) => (
                <div
                  key={p.id}
                  ref={(el) => (itemsRef.current[i] = el)}
                  className="ring-item absolute left-1/2 top-1/2"
                  style={{ '--d': i, opacity: i === 0 ? 1 : Math.max(0, 1 - (i - 0.15) * 0.42), zIndex: 100 - i * 10 }}
                >
                  <button
                    type="button"
                    onClick={() => setSelected(p)}
                    className="block h-full w-full cursor-pointer focus-visible:outline-none"
                    aria-label={`View details: ${p.title}`}
                    tabIndex={-1}
                  >
                    <RingShot project={p} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* details of the client in front */}
          <div className="frame relative z-[110] mt-auto">
            <div key={active} className="grid gap-5 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-6">
                <p className="ring-in text-sm font-semibold" style={{ color: BRAND[cur.id], '--k': 0 }}>
                  {pad(active + 1)} / {pad(total)} &middot; {meta.place}
                </p>
                <h3 className="display mt-2 overflow-hidden text-[2.4rem] leading-[1] sm:text-6xl" aria-label={cur.title}>
                  {cur.title.split(' ').map((word, w, words) => {
                    const offset = words.slice(0, w).join(' ').length + (w ? 1 : 0);
                    return (
                      <span key={w} aria-hidden="true" className="inline-block whitespace-nowrap">
                        {word.split('').map((ch, k) => (
                          <span key={k} className="ring-letter inline-block" style={{ '--k': offset + k }}>
                            {ch}
                          </span>
                        ))}
                        {w < words.length - 1 ? '\u00A0' : ''}
                      </span>
                    );
                  })}
                </h3>
              </div>
              <div className="lg:col-span-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="ring-in" style={{ '--k': 3 }}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">Problem</p>
                    <p className="mt-1 text-neutral-300">{meta.problem}</p>
                  </div>
                  <div className="ring-in" style={{ '--k': 5 }}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ color: BRAND[cur.id] }}>What we built</p>
                    <p className="mt-1 font-medium">{meta.built}</p>
                  </div>
                </div>
                {reviewFor(cur.id) && (
                  <figure className="ring-in mt-4 border-l-2 pl-4" style={{ '--k': 6, borderColor: BRAND[cur.id] }}>
                    {reviewFor(cur.id).rating && <p className="text-xs tracking-[0.2em] text-[#F5B529]" aria-label="5 out of 5 stars">{'\u2605'.repeat(reviewFor(cur.id).rating)}</p>}
                    <blockquote className="line-clamp-2 text-sm italic leading-snug text-white/85 sm:text-[0.95rem]">&ldquo;{reviewFor(cur.id).content}&rdquo;</blockquote>
                    <figcaption className="mt-1 text-xs text-white/55">
                      {reviewFor(cur.id).name} &middot; {reviewFor(cur.id).role}
                    </figcaption>
                  </figure>
                )}
                <div className="ring-in mt-5 flex flex-wrap gap-3" style={{ '--k': 7 }}>
                  <button type="button" onClick={() => setSelected(cur)} className="btn h-11 rounded-full bg-white px-5 text-sm text-[#040A15] hover:bg-[#4FB0E7]">
                    View details
                  </button>
                  {cur.liveUrl && (
                    <a href={cur.liveUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-5 text-sm font-semibold transition-colors hover:bg-white/15">
                      Visit website <ArrowUpRight size={16} aria-hidden="true" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* client tabs + progress */}
            <div className="mt-6 sm:mt-8">
              <div className="relative h-px w-full bg-white/10">
                <div ref={barRef} className="absolute inset-y-0 left-0 w-full origin-left" style={{ transform: 'scaleX(0)', background: BRAND[cur.id], boxShadow: `0 0 12px ${BRAND[cur.id]}`, transition: 'background-color .5s' }} />
              </div>
              <ul className="-mx-1 mt-3 flex gap-1 overflow-x-auto pb-1 pr-20 [scrollbar-width:none] sm:pr-0 [&::-webkit-scrollbar]:hidden" aria-label="Jump to a client">
                {list.map((p, i) => (
                  <li key={p.id} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => goTo(i)}
                      aria-current={active === i ? 'true' : undefined}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors sm:text-sm ${active === i ? 'bg-white text-[#040A15]' : 'text-white/55 hover:text-white'}`}
                    >
                      {p.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="frame relative z-10 pb-24 pt-10 sm:pb-32">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-white/10 bg-gradient-to-r from-[#4FB0E7]/20 via-white/[0.04] to-transparent p-7 sm:p-10 md:flex-row md:items-center">
          <h4 className="display-semi text-2xl sm:text-3xl">Got a problem like these?</h4>
          <Link to="/contact" className="btn bg-[#4FB0E7] text-[#040A15] hover:bg-[#3ca0d6]">
            Start a project <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>

      <AnimatePresence>{selected && <ClientModal project={selected} onClose={close} />}</AnimatePresence>
    </section>
  );
}
